import { useState, type FormEvent } from "react";
import { useNavigate } from "react-router-dom";
import { AppLayout } from "@/components/layout/AppLayout";
import { PageHeader } from "@/components/layout/PageHeader";
import { PageContainer } from "@/components/layout/PageContainer";
import { ContactChannels } from "@/components/contact/ContactChannels";
import { ContactForm, type ContactFormValues } from "@/components/contact/ContactForm";
import { Card, CardContent } from "@/components/ui/card";
import { useToast } from "@/contexts/ToastContext";
import { getApiErrorMessage } from "@/lib/api-error";
import { contactApi } from "@/lib/api/contact";

const EMPTY_FORM: ContactFormValues = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

const ContactPage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [formData, setFormData] = useState<ContactFormValues>(EMPTY_FORM);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (field: keyof ContactFormValues, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (isSubmitting) return;

    setIsSubmitting(true);
    try {
      const result = await contactApi.submit(formData);
      if (!result.success) {
        toast.error(result.message || "Failed to send message. Please try again.");
        return;
      }
      toast.success(
        result.message || "Your message has been sent successfully. We’ll get back to you soon."
      );
      setFormData(EMPTY_FORM);
    } catch (error: unknown) {
      toast.error(getApiErrorMessage(error, "Failed to send message. Please try again."));
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <AppLayout>
      <PageContainer width="wide">
        <PageHeader
          title="Contact Support"
          description="Questions or issues? Send a message and our team will reply as soon as possible."
          breadcrumbItems={[
            { label: "Dashboard", href: "/dashboard" },
            { label: "Contact Support" },
          ]}
          showSearch={false}
          titleSize="md"
        />

        <div className="grid grid-cols-1 gap-5 lg:grid-cols-5 lg:gap-6">
          <Card className="border-slate-200 shadow-sm lg:col-span-3">
            <CardContent className="p-5 sm:p-6">
              <div className="mb-5">
                <h2 className="font-heading text-lg font-semibold text-slate-900">
                  Send a message
                </h2>
                <p className="mt-1 text-sm text-slate-500">
                  We typically respond within 24 hours on business days.
                </p>
              </div>
              <ContactForm
                formData={formData}
                isSubmitting={isSubmitting}
                onChange={handleChange}
                onSubmit={handleSubmit}
                onCancel={() => navigate(-1)}
              />
            </CardContent>
          </Card>

          <div className="lg:col-span-2">
            <ContactChannels />
          </div>
        </div>
      </PageContainer>
    </AppLayout>
  );
};

export default ContactPage;
